import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../../redux/store';
import { fetchCompetitorPromotionRequest } from '../../redux/slices/competitorPromotionSlice';
import PageHeader from '../common/PageHeader';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import EmptyState from '../common/EmptyState';

const CompetitorPromotions: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data, competitors, loading, error } = useSelector(
    (state: RootState) => state.competitorPromotion
  );
  const [expandedCompetitor, setExpandedCompetitor] = useState<string | null>(null);

  useEffect(() => {
    // Fetch competitor promotion data for the restaurant
    dispatch(fetchCompetitorPromotionRequest('4bebbb8a-a2bf-4d53-b287-842c3c5be847'));
  }, [dispatch]);

  if (loading) {
    return <LoadingState page="competitor promotions" />;
  }

  if (error) {
    return (
      <ErrorState 
        message="Failed to load competitor promotions" 
        error={error}
        onRetry={() => dispatch(fetchCompetitorPromotionRequest('4bebbb8a-a2bf-4d53-b287-842c3c5be847'))}
      />
    );
  }

  if (!data) {
    return <EmptyState message="No competitor promotion data available" />;
  }

  const toggleCompetitor = (competitorName: string) => {
    setExpandedCompetitor(expandedCompetitor === competitorName ? null : competitorName);
  };

  return (
    <div className="min-h-screen ">
      <PageHeader title="Competitor Promotions" />

      <div className="mt-6 space-y-6">
       

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Competitor Analysis */}
          <div className="bg-[#2a2a2a] rounded-lg p-6 border border-[#E0E0E021]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Competitor Analysis</h3>
              <span className="px-3 py-1 bg-[#ee4425] text-white text-xs rounded-full">
                {competitors.length} Competitors
              </span>
            </div>
            <p className="text-sm text-gray-400 mb-6">
              Detailed breakdown of competitor promotional strategies
            </p>

            <div className="space-y-4">
              {competitors.map((competitor, index) => (
                <div key={index} className="bg-[#3a3a3a] rounded-lg border border-[#4a4a4a]">
                  <button
                    onClick={() => toggleCompetitor(competitor.competitor_name)}
                    className="w-full p-4 flex items-start justify-between text-left cursor-pointer"
                  >
                    <div className="flex-1">
                      <h4 className="text-md font-normal text-gray-300">
                        {competitor.competitor_name}
                      </h4>
                      <div className="flex items-center gap-1 mt-1">
                        <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-xs text-gray-400">{competitor.address}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-[#ee4425]/30 text-white text-xs rounded-full">
                        {competitor.recent_promotions.length} Promotions
                      </span>
                      <svg
                        className={`w-5 h-5 text-orange-500 transition-transform ${
                          expandedCompetitor === competitor.competitor_name ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {expandedCompetitor === competitor.competitor_name && (
                    <div className="px-4 pb-4">
                      <h5 className="text-sm font-medium text-gray-400 mb-3">Recent Promotions</h5>
                      <div className="space-y-3">
                        {competitor.recent_promotions.map((promo, promoIndex) => (
                          <div key={promoIndex} className="bg-[#2a2a2a] rounded p-3 border border-[#4a4a4a]">
                            <div className="flex items-start justify-between mb-2">
                              <h6 className="font-medium text-white">{promo.promotion_name}</h6>
                              <span className="px-2 py-1 bg-orange-900/50 text-orange-300 text-[10px] rounded">
                                {promo.source}
                              </span>
                            </div>
                            <p className="text-xs mt-3 text-gray-300">{promo.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Promotion Description and Strategic Rationale */}
          <div className="space-y-6">
            {/* Promotion Description */}
            <div className="bg-[#2a2a2a] rounded-lg p-6 border border-[#E0E0E021]">
              <h3 className="text-xl font-semibold text-white mb-4">Promotion Description</h3>
              <p className="text-gray-300 leading-relaxed text-sm">{data.description}</p>
            </div>

            {/* Strategic Rationale */}
            <div className="bg-[#2a2a2a] rounded-lg p-6 border border-[#E0E0E021]">
              <h3 className="text-xl font-semibold text-white mb-4">Strategic Rationale</h3>
              <p className="text-gray-300 leading-relaxed text-sm">{data.rationale}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitorPromotions;
